import { NgZone } from '@angular/core';

/**
 * Utilitários para integração NgUpgrade (Angular + AngularJS)
 *
 * Padrão estabelecido pela POC: componentes Angular downgraded
 * não podem usar DI normal do Angular. O acesso a services é feito
 * via platformRef armazenado globalmente no bootstrap (main.ts).
 */

/**
 * Obtém um service Angular via platformRef global.
 * Usar no ngOnInit dos componentes downgraded.
 *
 * @example
 * this.vehicleService = getAngularService(VehicleService);
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- token de DI Angular (Type/InjectionToken), contrato NgUpgrade (§2.3); tipado na Exit Phase
export function getAngularService<T>(token: any): T | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ponte platformRef NgUpgrade (§2.3); tipado na Exit Phase
  const platformRef = (window as any).angularPlatformRef;
  if (!platformRef) {
    console.error('[NgUpgrade] platformRef não disponível. Verifique o bootstrap em main.ts.');
    return null;
  }
  return platformRef.injector.get(token) as T;
}

export function fromLegacyPromise<T>(promise: PromiseLike<T>): Promise<T> {
  const ngZone = getAngularService<NgZone>(NgZone);

  if (!ngZone) {
    return Promise.resolve(promise);
  }

  return new Promise<T>((resolve, reject) => {
    promise.then(
      (value) => ngZone.run(() => setTimeout(() => resolve(value))),
      (error) => ngZone.run(() => setTimeout(() => reject(error)))
    );
  });
}

/**
 * Contrato do `$cookies` do ngCookies — subset usado pelos services Angular.
 *
 * Contrato **único**: era declarado por arquivo, o que produziu duas interfaces
 * de mesmo nome e contratos divergentes (`{ get }` vs. `{ getObject }`). Ao
 * precisar de outro método do ngCookies (`put`, `putObject`, `remove`,
 * `getAll`), acrescente aqui — não redeclare a interface no service.
 */
export interface LegacyCookiesService {
  get(key: string): string | undefined;
  getObject<T>(key: string): T | undefined;
}

/**
 * Obtém um service do AngularJS ($injector) via platformRef global.
 *
 * @example
 * this.$mdDialog = getAngularJSService('$mdDialog');
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- default genérico p/ service AngularJS sem tipos (§2.3); tipado na Exit Phase
export function getAngularJSService<T = any>(serviceName: string): T | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ponte platformRef NgUpgrade (§2.3); tipado na Exit Phase
  const platformRef = (window as any).angularPlatformRef;
  if (!platformRef) {
    console.error('[NgUpgrade] platformRef não disponível.');
    return null;
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- cast exigido pelo injector.get('$injector') do NgUpgrade (§2.3); tipado na Exit Phase
    const $injector = platformRef.injector.get('$injector' as any);
    return $injector.get(serviceName) as T;
  } catch (error) {
    console.error(`[NgUpgrade] Erro ao obter service AngularJS "${serviceName}":`, error);
    return null;
  }
}
